import { createContext, use, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  CURRENCY,
  DEFAULT_TIMEZONE,
  formatDate,
  formatDateTime,
  formatMoney,
  formatNumber,
  formatTime,
  zonedDayKey,
  type AppContextDTO,
  type RegionalSettingsDTO,
} from '@lightbm/shared';
import { useAuth } from '@/features/auth/auth-context';
import { api } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-client';

const FALLBACK_REGIONAL: RegionalSettingsDTO = {
  currency: { ...CURRENCY },
  defaultLanguage: 'en',
  timezone: DEFAULT_TIMEZONE,
  dateFormat: 'DD/MM/YYYY',
  numberFormat: 'space_comma',
  fiscalYearStartMonth: 1,
};

const AppContext = createContext<AppContextDTO | null>(null);

/** Company identity and regional rules, available to every signed-in user. */
export function AppContextProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { data } = useQuery({
    queryKey: queryKeys.appContext,
    queryFn: () => api.get<AppContextDTO>('/app/context'),
    enabled: Boolean(user),
    staleTime: 5 * 60_000,
  });
  return <AppContext value={user ? (data ?? null) : null}>{children}</AppContext>;
}

export const useAppContext = () => use(AppContext);

export interface Formatters {
  regional: RegionalSettingsDTO;
  money: (value: number) => string;
  /** A TND amount without the currency, for columns whose header says TND: "13,200" */
  amount: (value: number) => string;
  number: (value: number, decimals?: number) => string;
  date: (value: string | Date) => string;
  time: (value: string | Date) => string;
  dateTime: (value: string | Date) => string;
  /** "Today 11:28", "Yesterday 17:40", "10 Oct", "10 Oct 2025" */
  moment: (value: string | Date) => string;
  /** "Tuesday 13 October 2026" in the interface language */
  longDate: (value: string | Date) => string;
  /** A calendar month "2026-10": "October 2026" in the interface language */
  month: (value: string) => string;
}

/** Numbers and dates the way the company configured them (CMP-04), in its time zone. */
export function buildFormatters(
  regional: RegionalSettingsDTO,
  locale: string,
  words: { today: string; yesterday: string },
): Formatters {
  const { timezone, dateFormat, numberFormat } = regional;
  const intlLocale = locale === 'fr' ? 'fr-FR' : 'en-GB';
  const dayMonth = new Intl.DateTimeFormat(intlLocale, {
    day: 'numeric',
    month: 'short',
    timeZone: timezone,
  });
  const dayMonthYear = new Intl.DateTimeFormat(intlLocale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: timezone,
  });
  const monthYear = new Intl.DateTimeFormat(intlLocale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
  const long = new Intl.DateTimeFormat(intlLocale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: timezone,
  });

  return {
    regional,
    money: (value) => formatMoney(value, numberFormat),
    amount: (value) => formatNumber(value, { decimals: CURRENCY.decimals, format: numberFormat }),
    number: (value, decimals = 0) => formatNumber(value, { decimals, format: numberFormat }),
    date: (value) => formatDate(value, timezone, dateFormat),
    time: (value) => formatTime(value, timezone),
    dateTime: (value) => formatDateTime(value, timezone, dateFormat),
    moment: (value) => {
      const now = new Date();
      const day = zonedDayKey(value, timezone);
      if (day === zonedDayKey(now, timezone))
        return `${words.today} ${formatTime(value, timezone)}`;
      const yesterday = new Date(now.getTime() - 24 * 3600 * 1000);
      if (day === zonedDayKey(yesterday, timezone))
        return `${words.yesterday} ${formatTime(value, timezone)}`;
      const sameYear = day.slice(0, 4) === zonedDayKey(now, timezone).slice(0, 4);
      return (sameYear ? dayMonth : dayMonthYear).format(new Date(value));
    },
    longDate: (value) => long.format(new Date(value)),
    // The 15th at noon UTC is in that month whatever the time zone.
    month: (value) => monthYear.format(new Date(`${value}-15T12:00:00Z`)),
  };
}

export function useFormatters(override?: RegionalSettingsDTO): Formatters {
  const context = useAppContext();
  const { t, i18n } = useTranslation();
  const regional = override ?? context?.regional ?? FALLBACK_REGIONAL;
  return useMemo(
    () =>
      buildFormatters(regional, i18n.language, {
        today: t('time.today'),
        yesterday: t('time.yesterday'),
      }),
    [regional, i18n.language, t],
  );
}
